import { useId, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { Popover } from '@mui/material';
import { styled } from '@mui/material/styles';

import { CHROME_PILL_H, type ChromeSize } from '../../components/ChromePill';
import Icon from '../../components/Icon';
import type { Neighborhood } from '../../shared/cityFile';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { PlaceSuggestion } from '../schedule/placeSearch';
import AddressSearch from './AddressSearch';
import { DistanceSlider, ModeToggle } from './controls';
import { MODE_LABEL, countSentence, formatMinutes, shortPlace } from './estimate';
import type { AddressOrigin, Home, LocateStatus, OriginKind } from './home';
import HomePicker from './HomePicker';
import { PickerButton, TextButton } from './styles';
import type { TravelMode } from './walkshed';

/**
 * The map's glance button (contract §16): where from, how, and how far, at a
 * glance ("🏠 🚲 30 min"), and a tap away from changing any of it. The popover
 * holds, in order, the mode toggle, the minutes slider with its live count,
 * and the origin: Current location, Home, or an address searched here.
 *
 * It is the phone's only distance control on the map; on a desktop it mirrors
 * the filters' distance section, and both edit the same state. A typed address
 * is measured from for this session only; it becomes the home only when Set
 * home is pressed. `onOpen('home')`, on the ref, opens it on the Home field:
 * the List's "Sort by nearest" does that when there is nothing to measure from.
 */

/** Where the popover opens: `home` is the origin rows, the picker showing if there is no home yet. */
export type MapFilterField = 'home';

export interface MapFilterButtonHandle {
  /** Opens the popover; `'home'` opens it on the Home field, the picker showing. */
  onOpen: (field?: MapFilterField) => void;
}

export interface MapFilterButtonProps {
  /** The origin chosen (`FilterState.origin`). */
  origin: OriginKind;
  /** The origin actually measured from: Current falls back to home until a position arrives. Null: nothing to measure from. */
  resolved: OriginKind | null;
  mode: TravelMode;
  /** One of `DISTANCE_STEPS`; null is any distance. */
  minutes: number | null;
  onChangeMode: (mode: TravelMode) => void;
  onChangeMinutes: (minutes: number | null) => void;
  onChangeOrigin: (origin: OriginKind) => void;
  home: Home | null;
  onSetHome: (home: Omit<Home, 'mode'>) => void;
  /** The session's searched place, if any. */
  address: AddressOrigin | null;
  /** Measure from this place (and select it as the origin). */
  onSetAddress: (address: AddressOrigin) => void;
  locate: LocateStatus;
  canLocate: boolean;
  /** Ask for the device's position; selects Current when it arrives. */
  onLocate: () => void;
  neighborhoods: Neighborhood[];
  /** `facetCounts().withinMinutes`, for the live count over the slider. */
  counts?: Record<number | 'any', number>;
  /** A solid background, for a button drawn straight over the map rather than on the header's gradient. */
  floating?: boolean;
  /** The header's pill height (`ChromePill`): the phone's or the strip's. */
  size?: ChromeSize;
  /** The popover was dismissed: the List's "ask" (see `HeaderAction`) gives the slot back. */
  onClose?: () => void;
  ref?: Ref<MapFilterButtonHandle>;
}

// --- the glance pill --------------------------------------------------------------

const Pill = styled('button', { shouldForwardProp: (p) => p !== '$floating' && p !== '$size' })<{ $floating: boolean; $size: ChromeSize }>(
  ({ $floating, $size }) => ({
    appearance: 'none',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    height: CHROME_PILL_H[$size],
    boxSizing: 'border-box',
    padding: $size === 'phone' ? '0 10px 0 8px' : '0 12px 0 9px',
    // The header's ground (ChromePill's), so it reads over the map's gradient.
    background: $floating ? 'rgba(54,47,42,0.92)' : 'rgba(36,31,28,0.35)',
    color: BRAND.parchment,
    border: '1.5px solid rgba(243,238,231,0.7)',
    borderRadius: 999,
    boxShadow: $floating ? '0 3px 12px rgba(36,31,28,0.35)' : 'none',
    cursor: 'pointer',
    fontFamily: FONTS.body,
    fontSize: $size === 'phone' ? 13 : 13.5,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '@media (hover: hover)': {
      '&:hover': { background: $floating ? 'rgba(74,65,58,0.95)' : 'rgba(243,238,231,0.12)', borderColor: BRAND.parchment },
    },
    '&[aria-expanded="true"]': { borderColor: BRAND.parchment },
    '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2 },
  }),
);

/** The house, as the home marker draws it. */
function HouseGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="8 8 18 17" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      <path d="M17 8.5 8.5 16.2h2.8v8.3h4.3v-5.2h2.8v5.2h4.3v-8.3h2.8z" fill="currentColor" />
    </svg>
  );
}

/** The current-location dot, in the map marker's blue so the two read as one thing. */
function HereGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 30.67 30.67" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      <circle cx="15.34" cy="15.34" r="15.34" fill="#4084fe" fillOpacity=".3" />
      <circle cx="15.34" cy="15.34" r="8.05" fill="#4084fe" stroke="#fff" strokeWidth="2.2" />
    </svg>
  );
}

/** A map pin: a searched address. */
function PlaceGlyph({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="10 8 14 18" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      <path
        d="M17 8.2c-3.4 0-6.1 2.7-6.1 6 0 4.3 6.1 11 6.1 11s6.1-6.7 6.1-11c0-3.3-2.7-6-6.1-6zm0 8.2a2.2 2.2 0 1 1 0-4.4 2.2 2.2 0 0 1 0 4.4z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
}

const ORIGIN_GLYPH: Record<OriginKind, () => React.JSX.Element> = {
  home: () => <HouseGlyph />,
  current: () => <HereGlyph />,
  address: () => <PlaceGlyph />,
};

/** The transport artwork (`_Dev process/transport type - …`), which Session 8 also names `travel:*`. */
const MODE_ICON: Record<TravelMode, string> = {
  walk: 'transport:walk',
  bike: 'transport:bike',
  transit: 'transport:transit',
};

// --- the popover --------------------------------------------------------------------

const Paper = {
  sx: {
    width: 340,
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100dvh - 32px)',
    overflowY: 'auto',
    padding: '16px 16px 14px',
    borderRadius: '14px',
    background: BRAND.mocha,
    color: BRAND.parchment,
    boxShadow: '0 8px 28px rgba(36,31,28,0.45)',
    boxSizing: 'border-box',
  },
};

const Heading = styled('h3')({
  margin: '18px 0 8px',
  fontFamily: FONTS.body,
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: 'rgba(243,238,231,0.7)',
  '&:first-of-type': { marginTop: 0 },
});

const Count = styled('div')({
  marginTop: 14,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
});

const Options = styled('div')({ display: 'flex', flexDirection: 'column', gap: 4 });

const Option = styled('button')({
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minHeight: 38,
  padding: '7px 10px',
  border: '1px solid transparent',
  borderRadius: 10,
  background: 'transparent',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontSize: 13.5,
  fontWeight: 600,
  textAlign: 'left',
  cursor: 'pointer',
  '& > span': { minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  '& small': { fontSize: 12, fontWeight: 500, opacity: 0.7 },
  '&[aria-pressed="true"]': { background: 'rgba(243,238,231,0.12)', borderColor: 'rgba(243,238,231,0.45)' },
  '@media (hover: hover)': { '&:not(:disabled):not([aria-pressed="true"]):hover': { background: 'rgba(243,238,231,0.07)' } },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 1 },
});

const Indent = styled('div')({ padding: '8px 2px 4px 10px' });

const AddressRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  marginTop: -2,
  padding: '0 2px',
});

// --- the component ----------------------------------------------------------------------

export default function MapFilterButton({
  origin,
  resolved,
  mode,
  minutes,
  onChangeMode,
  onChangeMinutes,
  onChangeOrigin,
  home,
  onSetHome,
  address,
  onSetAddress,
  locate,
  canLocate,
  onLocate,
  neighborhoods,
  counts,
  floating = false,
  size = 'desktop',
  onClose,
  ref,
}: MapFilterButtonProps) {
  const button = useRef<HTMLButtonElement | null>(null);
  /** Where the popover hangs from while it is open. */
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  /** The home picker, shown under Home: to set a first home, or to change it. */
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const ids = useId();

  const openOn = (field?: MapFilterField) => {
    setPicking(field === 'home' && (!home || origin === 'home'));
    setAnchor(button.current);
  };
  useImperativeHandle(ref, () => ({ onOpen: openOn }));

  const close = () => {
    setAnchor(null);
    setPicking(false);
    onClose?.();
  };

  const measurable = resolved != null;
  const count = counts ? (minutes == null ? counts.any : (counts[minutes] ?? 0)) : null;
  const glanceOrigin = resolved ?? 'home';
  const glanceMinutes = !measurable ? 'Set home' : minutes == null ? 'Any' : formatMinutes(minutes);
  const originWords =
    resolved === 'current' ? 'current location' : resolved === 'address' && address ? shortPlace(address.label) : 'home';
  const label = measurable
    ? `Travel: ${minutes == null ? 'any distance' : `within ${formatMinutes(minutes)}`} by ${MODE_LABEL[mode].toLowerCase()} from ${originWords}. Change`
    : 'Set where travel times are measured from';

  const chooseAddress = (s: PlaceSuggestion) => {
    // The row above names it now; the box is free for another.
    setQuery('');
    setPicking(false);
    onSetAddress({ label: s.label, point: s.coords });
  };

  return (
    <>
      <Pill
        ref={button}
        type="button"
        $floating={floating}
        $size={size}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label.replace(/\. Change$/, '')}
        onClick={() => (open ? close() : openOn())}
      >
        {ORIGIN_GLYPH[glanceOrigin]()}
        <Icon name={MODE_ICON[mode]} size={16} />
        <span>{glanceMinutes}</span>
      </Pill>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={12}
        slotProps={{ paper: { ...Paper, role: 'dialog', 'aria-label': 'Travel time' } as object }}
      >
        <Heading>Travel by</Heading>
        <ModeToggle mode={mode} onChange={onChangeMode} disabled={!measurable} />

        <Count aria-live="polite">
          {measurable && count != null ? countSentence(count, minutes) : measurable ? 'Travel time' : 'Choose where from, below'}
        </Count>
        <DistanceSlider minutes={minutes} onChange={onChangeMinutes} disabled={!measurable} />

        <Heading>From</Heading>
        <Options role="group" aria-label="Measure from">
          <Option
            type="button"
            aria-pressed={resolved === 'current'}
            disabled={!canLocate || locate === 'locating'}
            onClick={() => (locate === 'ready' ? onChangeOrigin('current') : onLocate())}
          >
            <HereGlyph size={17} />
            <span>
              {locate === 'locating' ? 'Finding you…' : 'Current location'}
              {locate === 'failed' && <small> · couldn’t get it</small>}
            </span>
          </Option>

          <Option
            type="button"
            aria-pressed={resolved === 'home'}
            onClick={() => {
              if (home) {
                onChangeOrigin('home');
                setPicking(false);
              } else setPicking(true);
            }}
          >
            <HouseGlyph size={17} />
            <span>
              {home ? (
                <>
                  Home <small>· {shortPlace(home.label)}</small>
                </>
              ) : (
                'Set a home'
              )}
            </span>
          </Option>
          {home && !picking && (
            <AddressRow>
              <TextButton type="button" onClick={() => setPicking(true)}>
                Change home
              </TextButton>
            </AddressRow>
          )}
          {picking && (
            <Indent>
              <HomePicker
                neighborhoods={neighborhoods}
                home={home}
                autoFocus
                onSetHome={(h) => {
                  onSetHome(h);
                  setPicking(false);
                }}
                onCancel={home ? () => setPicking(false) : undefined}
              />
            </Indent>
          )}

          {address && (
            <Option type="button" aria-pressed={resolved === 'address'} onClick={() => onChangeOrigin('address')}>
              <PlaceGlyph size={17} />
              <span title={address.label}>{shortPlace(address.label)}</span>
            </Option>
          )}
          {address && resolved === 'address' && (
            <AddressRow>
              <PickerButton
                type="button"
                style={{ padding: '4px 12px', fontSize: 12 }}
                onClick={() => onSetHome({ label: address.label, lat: address.point[0], lng: address.point[1], source: 'address' })}
              >
                Set as home
              </PickerButton>
              <small style={{ fontFamily: FONTS.body, fontSize: 11.5, opacity: 0.7 }}>For this visit only until you do.</small>
            </AddressRow>
          )}
        </Options>

        <div style={{ marginTop: 14 }}>
          <AddressSearch
            id={`${ids}-addr`}
            label={address ? 'Or another address' : 'Or an address'}
            query={query}
            onQuery={setQuery}
            onChoose={chooseAddress}
          />
        </div>
      </Popover>
    </>
  );
}
